import React, { useState } from 'react'
import { supabase } from '../lib/supabase'

const s = {
  shell: {
    minHeight: '100vh', background: '#f4f6f9',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '40px 20px', fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    WebkitFontSmoothing: 'antialiased',
  },
  card: {
    background: '#fff', border: '1px solid rgba(10,24,42,.1)',
    borderRadius: 20, padding: '44px 40px', width: '100%', maxWidth: 420,
    boxShadow: '0 20px 60px -20px rgba(10,24,42,.12)',
  },
  logo: {
    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32,
  },
  logomark: {
    width: 32, height: 32, borderRadius: 8, flexShrink: 0,
    background: 'linear-gradient(140deg,#0A1E30 0%,#061224 100%)',
    border: '1px solid #0C1A2C', display: 'grid', placeItems: 'center',
    position: 'relative', overflow: 'hidden',
  },
  logoText: {
    fontSize: 16, fontWeight: 500, letterSpacing: '-0.01em', color: '#0C1A2C',
  },
  h1: {
    fontSize: 28, fontWeight: 400, letterSpacing: '-0.02em', color: '#0C1A2C',
    margin: '0 0 6px', fontFamily: "'Instrument Serif', Georgia, serif",
  },
  sub: { fontSize: 14, color: '#6A84A2', margin: '0 0 28px', lineHeight: 1.5 },
  label: {
    display: 'block', fontSize: 10.5, letterSpacing: '.14em', textTransform: 'uppercase',
    color: '#6A84A2', marginBottom: 8, fontFamily: 'var(--mono, monospace)',
  },
  inputWrap: { position: 'relative', marginBottom: 16 },
  input: {
    width: '100%', background: '#f4f6f9', border: '1px solid rgba(10,24,42,.14)',
    borderRadius: 10, padding: '12px 16px 12px 42px', fontSize: 14,
    color: '#0C1A2C', outline: 'none', boxSizing: 'border-box',
    transition: 'border-color .2s ease', fontFamily: 'inherit',
  },
  inputIcon: {
    position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
    width: 16, height: 16, color: '#6A84A2', pointerEvents: 'none',
  },
  eyeBtn: {
    position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer', color: '#6A84A2',
    padding: 0, display: 'grid', placeItems: 'center',
  },
  btn: {
    width: '100%', background: '#0C1A2C', color: '#fff', border: '1px solid #0C1A2C',
    borderRadius: 999, padding: '14px 20px', fontSize: 14.5, fontWeight: 500,
    fontFamily: 'inherit', cursor: 'pointer', marginTop: 8,
    transition: 'background .2s ease',
  },
  error: {
    background: 'rgba(220,53,69,.07)', border: '1px solid rgba(220,53,69,.2)',
    color: '#b91c1c', fontSize: 13, borderRadius: 10, padding: '11px 14px', marginBottom: 20,
  },
  foot: { fontSize: 13, color: '#6A84A2', textAlign: 'center', marginTop: 20 },
}

export default function Login({ onLogin }) {
  const [form, setForm]         = useState({ email: '', password: '' })
  const [showPass, setShowPass] = useState(false)
  const [remember, setRemember] = useState(true)
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState('')

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const handleSubmit = async e => {
    e.preventDefault()
    setError('')
    setLoading(true)
    const { data, error: err } = await supabase.auth.signInWithPassword({
      email: form.email,
      password: form.password,
    })
    if (err) {
      setError(err.message)
    } else {
      if (!remember) {
        localStorage.setItem('vf_no_persist', '1')
        sessionStorage.setItem('vf_session_active', '1')
      } else {
        localStorage.removeItem('vf_no_persist')
      }
      onLogin(data.user)
    }
    setLoading(false)
  }

  return (
    <div style={s.shell}>
      <div style={s.card}>
        <div style={s.logo}>
          <span style={s.logomark}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 11l9-7 9 7"/><path d="M5 10v9h14v-9"/>
            </svg>
            <span style={{ position: 'absolute', top: 4, right: 4, width: 5, height: 5, borderRadius: '50%', background: '#0A7B6F', boxShadow: '0 0 8px rgba(10,123,111,.7)' }} />
          </span>
          <span style={s.logoText}>Value<span style={{ color: '#6A84A2', fontWeight: 400 }}>Fort</span></span>
        </div>

        <h1 style={s.h1}>Welcome back.</h1>
        <p style={s.sub}>Sign in to access your appraisal workspace.</p>

        {error && <div style={s.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <label style={s.label}>Email address</label>
          <div style={s.inputWrap}>
            <svg style={s.inputIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>
            <input
              style={s.input} type="email" required
              placeholder="john@example.com"
              value={form.email} onChange={set('email')}
              onFocus={e => e.target.style.borderColor = '#0A7B6F'}
              onBlur={e => e.target.style.borderColor = 'rgba(10,24,42,.14)'}
            />
          </div>

          <label style={s.label}>Password</label>
          <div style={s.inputWrap}>
            <svg style={s.inputIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            <input
              style={{ ...s.input, paddingRight: 44 }}
              type={showPass ? 'text' : 'password'}
              required placeholder="Your password"
              value={form.password} onChange={set('password')}
              onFocus={e => e.target.style.borderColor = '#0A7B6F'}
              onBlur={e => e.target.style.borderColor = 'rgba(10,24,42,.14)'}
            />
            <button type="button" style={s.eyeBtn} onClick={() => setShowPass(p => !p)}>
              {showPass
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 0' }}>
            <input
              id="remember" type="checkbox" checked={remember}
              onChange={e => setRemember(e.target.checked)}
              style={{ width: 15, height: 15, accentColor: '#0A7B6F', cursor: 'pointer', flexShrink: 0 }}
            />
            <label htmlFor="remember" style={{ fontSize: 13, color: '#6A84A2', cursor: 'pointer', userSelect: 'none' }}>
              Remember me
            </label>
          </div>

          <button
            type="submit" disabled={loading}
            style={{ ...s.btn, opacity: loading ? .55 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
            onMouseEnter={e => !loading && (e.target.style.background = '#1a2e45')}
            onMouseLeave={e => e.target.style.background = '#0C1A2C'}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p style={s.foot}>
          Don't have an account?{' '}
          <a href="https://valufort.com/signup"
            style={{ color: '#0A7B6F', textDecoration: 'none', fontWeight: 500 }}>
            Create account
          </a>
        </p>
      </div>
    </div>
  )
}
